import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DemoLayout from "@/components/demo/DemoLayout";
import { useTheme } from "@/contexts/ThemeContext";
import { AlertTriangle, Lock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/demo`;
const mono = { fontFamily: "var(--font-mono)" };
const FLAGS = {
  canada: "\u{1F1E8}\u{1F1E6}", usa: "\u{1F1FA}\u{1F1F8}", jamaica: "\u{1F1EF}\u{1F1F2}",
  barbados: "\u{1F1E7}\u{1F1E7}", trinidad_tobago: "\u{1F1F9}\u{1F1F9}", panama: "\u{1F1F5}\u{1F1E6}",
};

function formatActivityTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/* ── theme-aware style tokens ── */
const crd = {
  background: "var(--surface-card)",
  border: "var(--border-width) solid var(--border-default)",
  borderRadius: "1rem",
  boxShadow: "var(--shadow-card)",
  transition: "background 150ms ease, border-color 150ms ease",
};
const inner = { background: "var(--surface-inner)", borderRadius: "0.5rem" };
const track = { background: "var(--surface-muted)", borderRadius: "9999px" };
const tp = { color: "var(--text-primary)" };
const ts = { color: "var(--text-secondary)" };
const tm = { color: "var(--text-muted)" };
const bdr = { borderColor: "var(--border-default)" };
const labelS = { ...tm, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 600 };

/* ═══ ROW 0: HEADER ═══ */
function Header({ data }) {
  return (
    <div className="flex justify-between items-start mb-8" data-testid="dashboard-header">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight" style={tp}>Strategic Command Center</h1>
        <p className="text-sm mt-1" style={ts}>Sagicor Technology Capability Assessment &middot; Real-time Intelligence</p>
      </div>
      <div className="text-right">
        <div className="flex items-center gap-2 justify-end">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-xs text-green-500 font-medium">LIVE</span>
        </div>
        <p className="text-2xl font-semibold mt-1" style={{ ...tp, ...mono }}>
          {data.days_remaining} <span className="text-sm font-normal" style={ts}>days to readout</span>
        </p>
      </div>
    </div>
  );
}

/* ═══ ROW 1: RISK + METRICS ═══ */
function RiskAndMetrics({ data, isDark, taStats }) {
  const v = data.velocity || {};
  const riskMap = {
    "at-risk":  { grad: isDark ? "from-red-950/50 to-[#0a0a0a]"  : "from-red-50 to-white",  border: "border-red-500",  color: "text-red-500",  icon: "\u26A0" },
    "on-track": { grad: isDark ? "from-green-950/50 to-[#0a0a0a]": "from-green-50 to-white", border: "border-green-500",color: "text-green-500",icon: "\u2713" },
    "critical": { grad: isDark ? "from-red-950/80 to-[#0a0a0a]"  : "from-red-100 to-white",  border: "border-red-600",  color: "text-red-400",  icon: "\uD83D\uDEA8" },
  };
  const rs = riskMap[data.risk_status] || riskMap["at-risk"];

  const metrics = [
    { value: (v.sa_total || 498).toLocaleString(), label: "PROFESSIONALS", sub: "across 6 regions" },
    { value: `${data.sa?.rate || 0}%`, label: "SA COMPLETE", sub: "Self-Assessment", accent: "text-green-500" },
    { value: `${data.mv?.rate || 0}%`, label: "MV COMPLETE", sub: "Manager Validation", accent: "text-purple-500" },
    { value: `${((data.response_count || 0) / 1000).toFixed(1)}K`, label: "DATA POINTS", sub: "responses captured" },
  ];

  const ta = taStats?.technical_assessment || { total: 0, completed: 0 };
  const hos = taStats?.hands_on_simulation || { total: 0, completed: 0 };

  return (
    <div className="grid grid-cols-6 gap-4" data-testid="risk-and-metrics">
      {/* Risk indicator */}
      <div className={`bg-gradient-to-br ${rs.grad} ${rs.border} border rounded-2xl p-5 flex flex-col items-center justify-center`}>
        <div className={`w-16 h-16 rounded-full border-[3px] ${rs.border} flex items-center justify-center mb-3`}>
          <span className="text-2xl">{rs.icon}</span>
        </div>
        <p className={`text-sm font-semibold ${rs.color}`}>{data.risk_status?.toUpperCase().replace("-", " ")}</p>
        <p className="text-[10px] mt-1 text-center" style={tm}>{data.risk_message}</p>
      </div>
      {/* Metric cards */}
      {metrics.map((m, i) => (
        <div key={i} style={crd} className="p-5 flex flex-col items-center justify-center text-center">
          <p className={`text-2xl font-semibold tracking-tight ${m.accent || ""}`}
            style={{ ...mono, ...(m.accent ? {} : tp) }}>{m.value}</p>
          <p className="mt-2" style={labelS}>{m.label}</p>
          <p className="text-[10px] mt-0.5" style={tm}>{m.sub}</p>
        </div>
      ))}
      {/* TA / HOS Summary Card */}
      <div style={crd} className="p-5 flex flex-col items-center justify-center text-center" data-testid="metric-ta-hos">
        <p className="text-2xl font-semibold tracking-tight" style={{ ...mono, ...tp }}>
          {ta.total + hos.total > 0 ? `${Math.round((ta.completed + hos.completed) / (ta.total + hos.total) * 100)}%` : "0%"}
        </p>
        <p className="mt-2" style={labelS}>TA/HOS COMPLETE</p>
        <p className="text-[10px] mt-0.5" style={tm}>Technical Assessment</p>
        <p className="text-[10px]" style={tm}>& Hands-On Simulation</p>
      </div>
    </div>
  );
}

/* ═══ ROW 2: TRAJECTORY + PHASES ═══ */
function TrajectoryAndPhases({ data, isDark }) {
  const v = data.velocity || {};
  const saRate = data.sa?.rate || 0;

  const tData = (() => {
    // TICKETS 3+6 (2026-04-27): dynamic, calendar-aware x-axis + y-axis-safe values.
    // "Today" stays a literal string — it is the target of <ReferenceLine x="Today" />.
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const ago = (n) => new Date(Date.now() - n * 86400000);
    const ahead = (n) => new Date(Date.now() + n * 86400000);
    const safeRate = Math.min(100, Math.max(0, saRate || 0));
    const proj15 = safeRate + ((v.actual_daily || 5) * 27 / (data.sa?.total || 498)) * 100;
    const proj30 = safeRate + ((v.actual_daily || 5) * 42 / (data.sa?.total || 498)) * 100;
    return [
      { date: fmt(ago(60)), required: 10,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.07))) },
      { date: fmt(ago(45)), required: 25,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.20))) },
      { date: fmt(ago(30)), required: 40,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.40))) },
      { date: fmt(ago(15)), required: 55,  actual: Math.min(100, Math.max(0, Math.round(safeRate * 0.65))) },
      { date: "Today",       required: 70, actual: safeRate },
      { date: fmt(ahead(15)), required: 90,  projected: Math.min(100, Math.max(0, proj15)) },
      { date: fmt(ahead(30)), required: 100, projected: Math.min(100, Math.max(0, proj30)) },
    ];
  })();

  const gridClr = isDark ? "#1f1f1f" : "#e5e7eb";
  const tickClr = isDark ? "#6b7280" : "#9ca3af";
  const phases = data.phases || [];

  return (
    <div className="grid grid-cols-5 gap-4" data-testid="trajectory-and-phases">
      {/* Trajectory chart */}
      <div className="col-span-3 p-6" style={crd}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <p className="text-sm font-semibold" style={tp}>Completion Trajectory</p>
            <p className="text-xs mt-0.5" style={ts}>SA track projected vs required pace</p>
          </div>
          <div className="flex items-center gap-4 text-[10px]" style={ts}>
            <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-blue-500 inline-block" /> Required</span>
            <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-green-500 inline-block" /> Actual</span>
            <span className="flex items-center gap-1.5"><span className="w-6 h-0.5 bg-red-500/50 inline-block border-t border-dashed border-red-500" /> Projected</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={tData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridClr} />
            <XAxis dataKey="date" tick={{ fill: tickClr, fontSize: 10 }} stroke={gridClr} />
            <YAxis tick={{ fill: tickClr, fontSize: 10 }} stroke={gridClr} domain={[0, 100]} allowDataOverflow={true} tickFormatter={v => `${v}%`} ticks={[0, 25, 50, 75, 100]} />
            <Tooltip contentStyle={{
              background: isDark ? "#111" : "#fff",
              border: `1px solid ${isDark ? "#1f1f1f" : "#e5e7eb"}`,
              borderRadius: 8, fontSize: 12,
              color: isDark ? "#fff" : "#111",
            }} />
            <ReferenceLine x="Today" stroke="#3b82f6" strokeDasharray="3 3" label={{ value: "TODAY", fill: "#3b82f6", fontSize: 9 }} />
            <Line type="monotone" dataKey="required" stroke="#3b82f6" strokeWidth={2} strokeDasharray="6 3" dot={false} />
            <Line type="monotone" dataKey="actual" stroke="#22c55e" strokeWidth={2.5} dot={{ fill: "#22c55e", r: 3 }} connectNulls />
            <Line type="monotone" dataKey="projected" stroke="#ef4444" strokeWidth={1.5} strokeDasharray="4 4" dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
        <div className="grid grid-cols-3 gap-4 mt-4 pt-3 border-t" style={bdr}>
          <div><p style={labelS}>Required</p><p className="text-sm font-semibold text-blue-500" style={mono}>{v.required_daily}/day</p></div>
          <div><p style={labelS}>Actual</p><p className="text-sm font-semibold text-green-500" style={mono}>{v.actual_daily}/day</p></div>
          <div><p style={labelS}>Projected</p>
            <p className={`text-sm font-semibold ${data.days_late > 0 ? "text-red-500" : "text-green-500"}`} style={mono}>
              {data.days_late > 0 ? `${data.days_late} days late` : "On time"}
            </p>
          </div>
        </div>
      </div>

      {/* Phase Tracker */}
      <div className="col-span-2 p-6" style={crd}>
        <p className="text-sm font-semibold mb-1" style={tp}>Assessment Phases</p>
        <p className="text-xs mb-5" style={ts}>Methodology Progress</p>
        <div className="space-y-4">
          {phases.map((p, i) => (
            <div key={i} className={p.locked ? "opacity-35" : ""}>
              <div className="flex justify-between items-center mb-1.5">
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold
                    ${p.locked ? "" : p.color === "green" ? "bg-green-500 text-black" : "bg-purple-500 text-black"}`}
                    style={p.locked ? { background: "var(--surface-muted)", color: "var(--text-muted)" } : {}}>
                    {p.locked ? <Lock className="w-3 h-3" /> : i + 1}
                  </div>
                  <span className="text-sm" style={tp}>{p.name}</span>
                </div>
                <span className={`text-sm font-semibold ${!p.locked && p.color === "green" ? "text-green-500" : !p.locked ? "text-purple-500" : ""}`}
                  style={{ ...mono, ...(p.locked ? tm : {}) }}>
                  {p.locked ? "Locked" : `${p.progress}%`}
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={track}>
                {!p.locked && (
                  <div className={`h-full rounded-full ${p.color === "green" ? "bg-gradient-to-r from-green-500 to-green-400" : "bg-gradient-to-r from-purple-500 to-purple-400"}`}
                    style={{ width: `${p.progress}%` }} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ ROW 3: REGIONAL + CALIBRATION ═══ */
function RegionalAndCalibration({ data, navigate, isDark }) {
  const regions = data.regions || [];
  const intel = data.intelligence || {};
  const sCfg = {
    green: { badge: "text-green-500 bg-green-500/15", bar: "from-green-500 to-green-400", label: "ON TRACK" },
    amber: { badge: "text-amber-500 bg-amber-500/15", bar: "from-amber-500 to-amber-400", label: "MONITOR" },
    red: { badge: "text-red-500 bg-red-500/15", bar: "from-red-500 to-red-400", label: "CRITICAL" },
    gray: { badge: "text-gray-500 bg-gray-500/15", bar: "from-gray-600 to-gray-500", label: "NOT STARTED" },
  };

  return (
    <div className="grid grid-cols-5 gap-4" data-testid="regional-calibration">
      {/* Regional bars */}
      <div className="col-span-3 p-6" style={crd}>
        <p className="text-sm font-semibold mb-5" style={tp}>Regional Performance</p>
        <div className="space-y-4">
          {regions.map((r) => {
            const sc = sCfg[r.health] || sCfg.gray;
            return (
              <div key={r.code}>
                <div className="flex justify-between items-center mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-base">{FLAGS[r.code]}</span>
                    <span className="text-sm" style={tp}>{r.name}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${sc.badge}`}>{sc.label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs" style={{ ...ts, ...mono }}>{r.completed}/{r.total}</span>
                    <span className={`text-sm font-semibold ${r.health === "green" ? "text-green-500" : r.health === "amber" ? "text-amber-500" : r.health === "red" ? "text-red-500" : "text-gray-500"}`} style={mono}>
                      {r.completion_rate}%
                    </span>
                  </div>
                </div>
                <div className="h-2 rounded overflow-hidden" style={track}>
                  <div className={`h-full bg-gradient-to-r ${sc.bar} rounded`} style={{ width: `${r.completion_rate}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Calibration Intelligence */}
      <div className="col-span-2 p-6" style={crd}>
        <div className="flex justify-between items-center mb-5">
          <p className="text-sm font-semibold" style={tp}>Calibration Intelligence</p>
          <button onClick={() => navigate("/executive/demo/heatmap")} className="text-[10px] text-blue-500 hover:underline flex items-center gap-1">
            Full Heatmap <ExternalLink className="w-3 h-3" />
          </button>
        </div>
        {/* Mini scatter */}
        <div className="p-4 mb-4" style={inner}>
          <div className="flex items-center justify-center h-24 relative">
            <div className="absolute inset-0 flex items-end justify-center">
              <div className="w-full h-px absolute top-1/2" style={{ background: "var(--border-emphasis)" }} />
              <div className="h-full w-px absolute left-1/2" style={{ background: "var(--border-emphasis)" }} />
            </div>
            {[
              { x: 30, y: 60, cat: "under" }, { x: 25, y: 65, cat: "under" }, { x: 35, y: 70, cat: "under" }, { x: 28, y: 62, cat: "under" },
              { x: 50, y: 52, cat: "aligned" }, { x: 55, y: 48, cat: "aligned" }, { x: 45, y: 50, cat: "aligned" }, { x: 52, y: 55, cat: "aligned" }, { x: 48, y: 47, cat: "aligned" },
              { x: 70, y: 45, cat: "over" }, { x: 75, y: 40, cat: "over" }, { x: 65, y: 38, cat: "over" }, { x: 80, y: 35, cat: "over" }, { x: 72, y: 42, cat: "over" },
              { x: 68, y: 50, cat: "over" }, { x: 78, y: 30, cat: "over" }, { x: 85, y: 38, cat: "over" },
            ].map((d, i) => (
              <div key={i} className={`absolute w-2 h-2 rounded-full ${d.cat === "under" ? "bg-blue-500" : d.cat === "aligned" ? "bg-gray-400" : "bg-red-500"}`}
                style={{ left: `${d.x}%`, bottom: `${d.y}%`, opacity: 0.8 }} />
            ))}
          </div>
          <div className="flex justify-between text-[9px] mt-1" style={tm}>
            <span>Self-Assessment &rarr;</span><span>&uarr; Manager</span>
          </div>
        </div>
        <div className="space-y-3">
          {[
            { label: "Hidden Talent", count: intel.under_raters || 0, color: "text-blue-500", dot: "bg-blue-500" },
            { label: "Well-Calibrated", count: intel.aligned || 0, dot: "bg-gray-400" },
            { label: "Coaching Needed", count: intel.over_raters || 0, color: "text-red-500", dot: "bg-red-500" },
          ].map((c) => (
            <div key={c.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${c.dot}`} />
                <span className="text-xs" style={ts}>{c.label}</span>
              </div>
              <span className={`text-sm font-semibold ${c.color || ""}`} style={{ ...mono, ...(c.color ? {} : ts) }}>{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ ROW 4: SKILLS + ACCELERATION + RISKS ═══ */
function SkillsAccelerationRisks({ data, isDark }) {
  const skills = data.skills_intel || {};
  const risks = data.engagement_risks || {};
  const v = data.velocity || {};

  const interventions = [
    { icon: "\u2713", name: "Regional Escalation Calls", desc: "Trinidad & Barbados leads", impact: 3.5, status: "active" },
    { icon: "\uD83D\uDCE7", name: "Targeted Reminder Campaign", desc: `${data.sa?.not_started || 0} not started + ${risks.stalled} stalled`, impact: 2.8, status: "scheduled" },
    { icon: "\uD83D\uDC64", name: "Manager Accountability Push", desc: `${(data.mv_bottleneck || []).length} managers with pending`, impact: 1.9, status: "planned" },
  ];
  const totalImpact = interventions.reduce((s, i) => s + i.impact, 0);

  const stClr = { active: "#22c55e", scheduled: "#3b82f6", planned: "#a855f7" };
  const stBg = { active: "bg-green-500/10", scheduled: "bg-blue-500/10", planned: "bg-purple-500/10" };
  const stBadge = { active: "text-green-500 bg-green-500/15", scheduled: "text-blue-500 bg-blue-500/15", planned: "text-purple-500 bg-purple-500/15" };
  const stText = { active: "text-green-500", scheduled: "text-blue-500", planned: "text-purple-500" };

  return (
    <div className="grid grid-cols-3 gap-4" data-testid="skills-acceleration-risks">
      {/* Skills Intel */}
      <div className="p-6" style={crd}>
        <p className="text-sm font-semibold mb-5" style={tp}>Skills Intelligence</p>
        {(skills.strongest || []).length > 0 && (
          <div className="mb-5">
            <p className="text-[10px] text-green-500 uppercase tracking-wider font-semibold mb-2.5">Strongest</p>
            {skills.strongest.map((s, i) => (
              <div key={i} className="mb-2.5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs" style={tp}>{s.name}</span>
                  <span className="text-sm font-semibold text-green-500" style={mono}>{s.score}</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={track}>
                  <div className="h-full bg-green-500 rounded-full" style={{ width: `${s.score * 20}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
        {(skills.gaps || []).length > 0 && (
          <div>
            <p className="text-[10px] text-red-500 uppercase tracking-wider font-semibold mb-2.5">Skill Gaps</p>
            {skills.gaps.map((s, i) => (
              <div key={i} className="mb-2.5">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs" style={tp}>{s.name}</span>
                  <span className="text-sm font-semibold text-red-500" style={mono}>{s.score}</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={track}>
                  <div className="h-full bg-red-500 rounded-full" style={{ width: `${s.score * 20}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acceleration Plan */}
      <div className="p-6" style={crd}>
        <div className="flex justify-between items-center mb-5">
          <div>
            <p className="text-sm font-semibold" style={tp}>Acceleration Plan</p>
            <p className="text-[10px] mt-0.5" style={ts}>Close the velocity gap</p>
          </div>
          <div className="text-right">
            <p style={labelS}>Impact</p>
            <p className="text-base font-semibold text-green-500" style={mono}>+{totalImpact.toFixed(1)}/day</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {interventions.map((it, i) => (
            <div key={i} className={`p-3 rounded-lg border-l-[3px] ${stBg[it.status]}`}
              style={{ borderLeftColor: stClr[it.status] }}>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs">{it.icon}</span>
                <span className="text-xs font-medium" style={tp}>{it.name}</span>
                <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${stBadge[it.status]}`}>{it.status.toUpperCase()}</span>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-[10px]" style={ts}>{it.desc}</p>
                <p className={`text-xs font-semibold ${stText[it.status]}`} style={mono}>+{it.impact}/day</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-[10px] text-green-500 font-medium">With all interventions</p>
              <p className="text-[10px]" style={ts}>{v.actual_daily} + {totalImpact.toFixed(1)} = <strong style={tp}>{(v.actual_daily + totalImpact).toFixed(1)}/day</strong></p>
            </div>
            <p className="text-sm font-semibold text-green-500">Apr 28 &#10003;</p>
          </div>
        </div>
      </div>

      {/* Engagement Risks */}
      <div className="p-6" style={crd}>
        <p className="text-sm font-semibold mb-5" style={tp}>Engagement Risks</p>
        <div className="space-y-4">
          {[
            { label: "Stalled Assessments", value: risks.stalled || 0, desc: "Started >7 days ago, no activity", color: "text-amber-500", bg: "bg-amber-500/10", border: "border-amber-500/20" },
            { label: "Abandonment Risk", value: risks.abandonment_risk || 0, desc: "Started >14 days ago, stale", color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20" },
            { label: "Low Sentiment", value: risks.low_sentiment || 0, desc: "Communication satisfaction \u22642", color: "text-purple-500", bg: "bg-purple-500/10", border: "border-purple-500/20" },
          ].map((r) => (
            <div key={r.label} className={`p-3 ${r.bg} border ${r.border} rounded-lg`}>
              <div className="flex justify-between items-center">
                <span className="text-xs" style={ts}>{r.label}</span>
                <span className={`text-lg font-semibold ${r.color}`} style={mono}>{r.value}</span>
              </div>
              <p className="text-[10px] mt-0.5" style={tm}>{r.desc}</p>
            </div>
          ))}
        </div>
        <button onClick={() => toast.info("Nudge Campaign", { description: "Delivered on assessment completion" })} className="mt-4 w-full py-2.5 text-xs font-semibold text-amber-500 bg-amber-500/10 border border-amber-500/20 rounded-xl hover:bg-amber-500/20 transition-colors">
          Send Nudge Campaign &rarr;
        </button>
      </div>
    </div>
  );
}

/* ═══ ROW 5: ASPIRATION + LIVE ACTIVITY ═══ */
function AspirationAndActivity({ data }) {
  const asp = data.aspiration_pipeline || {};
  const activity = data.live_activity || [];

  const pipeline = [
    { label: "VP+", count: asp.vp_plus || 0, color: "bg-purple-500" },
    { label: "Director", count: asp.director || 0, color: "bg-blue-500" },
    { label: "Tech Lead", count: asp.tech_lead || 0, color: "bg-green-500" },
    { label: "Lateral Move", count: asp.lateral || 0, color: "bg-amber-500" },
    { label: "No Clear Path", count: asp.no_path || 0, color: "bg-gray-500" },
  ];
  const maxCount = Math.max(...pipeline.map(p => p.count), 1);

  return (
    <div className="grid grid-cols-2 gap-4" data-testid="aspiration-activity">
      {/* Leadership Pipeline */}
      <div className="p-6" style={crd}>
        <p className="text-sm font-semibold mb-1" style={tp}>Leadership Pipeline</p>
        <p className="text-xs mb-5" style={ts}>Career aspiration distribution from SA responses</p>
        <div className="space-y-3">
          {pipeline.map((p) => (
            <div key={p.label} className="flex items-center gap-3">
              <span className="text-xs w-24 text-right" style={ts}>{p.label}</span>
              <div className="flex-1 h-5 rounded overflow-hidden" style={track}>
                <div className={`h-full ${p.color} rounded flex items-center justify-end pr-2`}
                  style={{ width: `${Math.max((p.count / maxCount) * 100, 8)}%` }}>
                  <span className="text-[10px] font-semibold text-white" style={mono}>{p.count}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Live Activity */}
      <div className="p-6" style={crd}>
        <div className="flex items-center gap-2 mb-5">
          <p className="text-sm font-semibold" style={tp}>Live Activity</p>
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        </div>
        <div className="space-y-2">
          {activity.slice(0, 6).map((a, i) => (
            <div key={i} className="p-3 flex items-center justify-between" style={inner}>
              <div className="flex items-center gap-2">
                <span className="text-sm">{FLAGS[a.region] || "\uD83C\uDF10"}</span>
                <span className="text-xs truncate max-w-[150px]" style={tp}>{a.name}</span>
              </div>
              <div className="flex items-center gap-2">
                {a.time && (
                  <span className="text-[10px]" style={tm}>{formatActivityTime(a.time)}</span>
                )}
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold
                  ${a.status === "completed" ? "text-green-500 bg-green-500/15" : "text-amber-500 bg-amber-500/15"}`}>
                  {a.status === "completed" ? "Completed" : "In Progress"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══ ROW 6: EXECUTIVE ACTION QUEUE ═══ */
function ActionQueue({ data, isDark }) {
  const alerts = data.alerts || [];
  const typeS = {
    critical: { bg: "bg-red-500/10", border: "border-red-500/30", badge: "text-red-500", label: "ESCALATE" },
    warning: { bg: "bg-amber-500/10", border: "border-amber-500/30", badge: "text-amber-500", label: "REMIND" },
  };

  const actions = alerts.slice(0, 5).map((a) => ({ ...a, ...(typeS[a.type] || typeS.warning) }));
  if (actions.length === 0) return null;

  return (
    <div className="p-6 rounded-2xl" data-testid="action-queue"
      style={{
        background: isDark
          ? "linear-gradient(to bottom right, #111111, #0a0a0a)"
          : "linear-gradient(to bottom right, #ffffff, #f8f9fb)",
        border: "var(--border-width) solid var(--border-default)",
        boxShadow: "var(--shadow-card)",
      }}>
      <div className="flex justify-between items-center mb-5">
        <p className="text-sm font-semibold" style={tp}>Executive Action Queue</p>
        <span className="text-[10px] text-white bg-red-500 px-2.5 py-0.5 rounded-full font-semibold" style={mono}>
          {actions.length} actions
        </span>
      </div>
      <div className="grid grid-cols-5 gap-3">
        {actions.map((a, i) => (
          <div key={i} className={`p-4 ${a.bg} border ${a.border} rounded-xl cursor-pointer hover:scale-[1.02] transition-transform`}>
            <div className="flex items-center gap-1.5 mb-2">
              <AlertTriangle className={`w-3 h-3 ${a.badge}`} />
              <span className={`text-[9px] font-semibold ${a.badge}`}>{a.label}</span>
            </div>
            <p className="text-xs" style={tp}>{a.title}</p>
            <p className="text-[10px] mt-1 line-clamp-2" style={tm}>{a.recommended_action}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══ ASSESSMENT RESULTS BANNER (Sagicor brand strip) ═══ */
function AssessmentResultsBanner({ navigate }) {
  return (
    <div
      data-testid="assessment-results-banner"
      onClick={() => navigate("/executive/xray-cohort")}
      className="cursor-pointer transition-shadow hover:shadow-lg"
      style={{
        background: "var(--surface-card)",
        border: "var(--border-width) solid var(--border-default)",
        borderRadius: "1rem",
        boxShadow: "var(--shadow-card)",
        overflow: "hidden",
      }}
    >
      <div style={{ height: 4, background: "linear-gradient(90deg, #00B5EF 0%, #82C341 100%)" }} />
      <div className="flex justify-between items-center" style={{ padding: "20px 24px" }}>
        <div className="flex items-center gap-4">
          <img
            src="/branding/sagicor-logo-stacked.png"
            alt="Sagicor"
            style={{ height: 52, width: "auto" }}
          />
          <div>
            <div className="text-[10px] font-semibold tracking-widest uppercase" style={{ color: "#0099CC" }}>
              Sub-Phase A/B · Methodology v4.0
            </div>
            <div className="text-2xl font-semibold tracking-tight mt-1" style={tp}>
              Assessment Results
            </div>
            <div className="text-sm mt-1" style={ts}>
              View generated candidate X-Rays · click through to the full template
            </div>
          </div>
        </div>
        <div
          className="text-xs font-semibold tracking-wide"
          style={{
            padding: "10px 18px",
            background: "linear-gradient(135deg, #00B5EF 0%, #82C341 100%)",
            color: "#FFFFFF",
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,153,204,0.30)",
            whiteSpace: "nowrap",
          }}
        >
          Open Cohort →
        </div>
      </div>
    </div>
  );
}

/* ═══ MAIN PAGE ═══ */
export default function DemoCommandCenter() {
  const [data, setData] = useState(null);
  const [taStats, setTaStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { isDark } = useTheme();

  useEffect(() => {
    // Show cached data instantly, then refresh from API
    try {
      const cached = localStorage.getItem("dash_commandcenter");
      if (cached) { setData(JSON.parse(cached)); setLoading(false); }
    } catch {}

    const token = localStorage.getItem("executiveToken") || localStorage.getItem("authToken");
    const authHeaders = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

    Promise.all([
      axios.get(`${API}/dashboard-stats`),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/executive/ta-hos-stats`, authHeaders).catch(() => ({ data: null })),
    ])
      .then(([statsRes, taRes]) => {
        setData(statsRes.data);
        setTaStats(taRes.data);
        try { localStorage.setItem("dash_commandcenter", JSON.stringify(statsRes.data)); } catch {}
      })
      .catch(e => console.error("Dashboard load error", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <DemoLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-sm" style={ts}>Loading Command Center...</div>
        </div>
      </DemoLayout>
    );
  }

  return (
    <DemoLayout alertCount={data.alerts?.length || 0}>
      <div className="space-y-4" data-testid="command-center">
        <Header data={data} />
        <AssessmentResultsBanner navigate={navigate} />
        <RiskAndMetrics data={data} isDark={isDark} taStats={taStats} />
        <TrajectoryAndPhases data={data} isDark={isDark} />
        <RegionalAndCalibration data={data} navigate={navigate} isDark={isDark} />
        <SkillsAccelerationRisks data={data} isDark={isDark} />
        <AspirationAndActivity data={data} />
        <ActionQueue data={data} isDark={isDark} />
      </div>
    </DemoLayout>
  );
}
